import {
  Avatar,
  Stack,
  Typography,
  Card,
  CardContent,
  IconButton,
  Box,
  Popper,
  Paper,
  ClickAwayListener,
  debounce,
  Skeleton,
  TextField,
  Autocomplete,
  InputAdornment,
  TooltipProps,
  tooltipClasses,
  Link,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridColumnGroupingModel,
  GridColumnHeaders,
  GridPaginationModel,
  GridRenderCellParams,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridLogicOperator,
  GridToolbar,
  useGridApiContext,
  useGridRootProps,
  GridSortModel,
  GridRowParams,
  GridCell,
} from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useRef, useState, useCallback, memo } from 'react';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings/selectors';
import Iconify from 'src/components/iconify';
import { FilterPanelCustom } from 'src/components/data-grid/filter-panel-custom';
import { useLocales } from 'src/locales';
import { useNonBrandedContext } from '../context/use-non-branded';
import { useExportMutation } from 'src/context/api/exports/api';
import { enqueueSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { useBuildColumns } from './hook';
import { useCompetitorsQuery, useLazyCompetitorsQuery } from 'src/context/api/non-branded/api';
import { fDate } from 'src/utils/format-time';
import { useSelector } from 'react-redux';
import { RootState } from 'src/context/store';
import { useBoolean } from 'src/hooks/use-boolean';
import Chart, { useChart } from 'src/components/chart';
import { palette } from '../figma-palette';
import { fPercent } from 'src/utils/format-number';
import "./competitors.scss";

export default function SearchTermsGrid() {
  const { t } = useLocales();
  const workspace = useCurrentWorkspace();
  const [exportMutation] = useExportMutation();
  const { selectedRegion } = useNonBrandedContext();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 5,
    page: 0,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'monthly_searches', sort: 'desc' },
  ]);
  const filterStore = useSelector((state: RootState) => state.filters);
  const commonQuery = {
    devices: filterStore.devices,
    countries: selectedRegion ? [selectedRegion] : [],
    platforms: filterStore.platforms,
    workspace_id: workspace?.id as number,
    start_date: fDate(filterStore.start, 'yyyy-MM-dd'),
    end_date: fDate(filterStore.end, 'yyyy-MM-dd'),
  }
  const { data: competitorsGrid, isLoading, isError, isFetching } = useCompetitorsQuery(
    {
      query: "grid",
      page_size: paginationModel.pageSize,
      page: paginationModel.page,
      filter_model: filterModel.items,
      sort_field: sortModel[0]?.field,
      sort_order: sortModel[0]?.sort || 'desc',
      ...commonQuery,
    }
  );
  const evolutionView = useBoolean(false);
  const [selectedDomain, setSelectedDomain] = useState<string>("");
  const [getCompetitors, { data: competitorsGridEvolution, isLoading: isLoadingEvolution, isError: isErrorEvolution, isFetching: isFetchingEvolution }] = useLazyCompetitorsQuery();

  const handleRowClick = (params: GridRowParams & any) => {
    if (params.column.field === 'domain_name') {
      setSelectedDomain(params.row.domain_name);
      getCompetitors({
        ...commonQuery,
        selected_domain: params.row.domain_name,
        page_size: 100,
        query: "evol"
      });
      evolutionView.onTrue();
    }
  }

  const apiRef = useRef<any>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const columns = useBuildColumns('competitors');

  const check = (value: any) => {
    if (value === undefined) {
      return false;
    }
    if (value === null) {
      return false;
    }
    if (typeof value === 'number') {
      return value !== 0;
    }
    if (typeof value === 'string') {
      return value !== '';
    }
    if (Array.isArray(value)) {
      return value.some(check);
    }
    if (typeof value === 'object') {
      return check(Object.values(value));
    }
    return false;
  }

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };


  const Toolbar = (props: any) => {
    return (
      <Stack
        ref={toolbarRef}
        direction="row"
        alignItems="center"
        spacing={0}
        px={2.5}
        py={1.25}
        sx={{
          borderBottom: '1px solid rgba(145, 158, 171, 0.12)',
          minHeight: 46,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: '#333C47',
            textAlign: 'center',
            fontFamily: 'Public Sans',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '120%',
          }}
        >
          {t('dashboard.nonBrand.competitors')}
        </Typography>
        <Box flexGrow={1} />
        <Stack direction="row" alignItems="center" px={1} spacing={0.5}>
          <IconButton
            onClick={() => apiRef.current?.showPreferences('columns')}
            sx={{
              width: 26,
              height: 26,
              padding: 0,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Iconify icon="mingcute:calendar-week-line" width={18} height={18} />
          </IconButton>
          <IconButton
            onClick={() => apiRef.current?.showFilterPanel()}
            sx={{
              width: 26,
              height: 26,
              padding: 0,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Iconify icon="ic:round-filter-list" width={18} height={18} />
          </IconButton>
          <LoadingButton
            loading={isLoading || isFetching}
            startIcon={<Iconify icon="lets-icons:export" width={16} height={16} />}
            onClick={() => {
              if (competitorsGrid?.job_id) {
                exportMutation({
                  name: `[${workspace?.name}] (Non Branded) Competitors export`,
                  queryId: competitorsGrid?.job_id,
                  columns: columns
                    .map((column) => column.field)
                    .filter((column) => {
                      return (
                        true
                      );
                    }),
                  column_tr: columns.reduce(
                    (acc: Record<string, string>, column: GridColDef) => {
                      acc[column.field] = column.headerName as string;
                      return acc;
                    },
                    {} as Record<string, string>
                  ),
                  dimensions: ['domain_name'],
                })
                  .unwrap()
                  .then((res) => {
                    enqueueSnackbar(
                      "Export is running in background, you will be notified when it's done",
                      { variant: 'success' }
                    );
                  })
                  .catch((err) => {
                    enqueueSnackbar('Error exporting data', { variant: 'error' });
                  });
              } else {
                enqueueSnackbar('Unable to export this query', { variant: 'error' });
              }
            }}
            sx={{
              minWidth: 24,
              minHeight: 24,
              maxWidth: 24,
              maxHeight: 24,
              padding: 0,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
          </LoadingButton>
        </Stack>
      </Stack>
    );
  };

  const totals = useMemo(() => {
    return competitorsGridEvolution?.data?.reduce((acc: any, item: any) => {
      acc.visibility_rate += item.visibility_rate ?? 0;
      acc.paid_visibility_rate += item.paid_visibility_rate ?? 0;
      acc.organic_visibility_rate += item.organic_visibility_rate ?? 0;
      return acc;
    }, {
      visibility_rate: 0,
      paid_visibility_rate: 0,
      organic_visibility_rate: 0,
    });
  }, [competitorsGridEvolution]);

  const trads = {
    "visibility_rate": t("dashboard.nb_brand_grid.columns.title.visibility_rate"),
    "paid_visibility_rate": t("dashboard.nb_brand_grid.columns.title.paid_visibility_rate"),
    "organic_visibility_rate": t("dashboard.nb_brand_grid.columns.title.organic_visibility_rate"),
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: '1px solid rgba(145, 158, 171, 0.12)',
        overflow: 'hidden',
      }}
    >
      <CardContent
        sx={{
          px: 0,
          py: 0,
          overflow: 'visible',
          minHeight: isLoading || isFetching ? 600 : 'auto',
        }}
      >
        <DataGridPro
          classes={{
            root: 'search-term-grid',
            columnHeaders: 'search-term-grid-column-headers',
            columnHeader: 'search-term-grid-column-header',
          }}
          apiRef={apiRef}
          pinnedColumns={{ left: ['search_term'], right: ['opportunity'] }}
          loading={isLoading || isFetching}
          rows={isLoading || isFetching ? [] : competitorsGrid?.data || []}
          columns={columns}
          getRowHeight={() => 60}
          disableColumnFilter={true}
          disableColumnMenu={true}
          filterModel={filterModel}
          filterMode="server"
          sortingMode="server"
          disableColumnSelector={true}
          disableMultipleColumnsSorting={true}
          onSortModelChange={setSortModel}
          sortModel={sortModel}
          onFilterModelChange={setFilterModel}
          disableDensitySelector
          checkboxSelection={false}
          pagination
          autoHeight
          disableRowSelectionOnClick={true}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={competitorsGrid?.total_rows || 0}
          pageSizeOptions={[5, 10, 20, 50, 100]}
          sx={{
            '& .MuiDataGrid-toolbarContainer': {
              padding: 0,
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(145, 158, 171, 0.12)',
              minHeight: 53,
            },
          }}
          slots={{
            toolbar: Toolbar,
            filterPanel: (props) => (
              <FilterPanelCustom
                {...props}
                getColumnForNewFilter={(args) => (args ? args.columns[0].field : undefined)}
              />
            ),
            loadingOverlay(props, deprecatedLegacyContext) {
              return (
                <Skeleton variant="rectangular" height={60 * (paginationModel.pageSize + 2)} />
              );
            },
            cell: (props) => <GridCell {...props} onClick={handleRowClick.bind(null, props)} />,
          }}
          slotProps={{
            panel: {
              placement: "bottom-end",
            },
            toolbar: {
              printOptions: {
                disableToolbarButton: true,
              },
              csvOptions: {
                disableToolbarButton: true,
              },
            },
            filterPanel: {
              logicOperators: [GridLogicOperator.And, GridLogicOperator.Or],
            },
            loadingOverlay: {
              style: {
                backgroundColor: 'unset',
              },
            },
          }}
        />
      </CardContent>
      <Dialog
        open={evolutionView.value}
        onClose={evolutionView.onFalse}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: '#f5f8fb',
          },
        }}
      >

        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}><Avatar alt={selectedDomain} src={`https://www.google.com/s2/favicons?domain=${selectedDomain}&sz=32`} sx={{ width: 24, height: 24 }} /><Typography variant="h6">{selectedDomain}</Typography></Stack>
            <IconButton onClick={evolutionView.onFalse}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent
          sx={{
            height: 450,
            overflowY: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            px: 3,
            my: 3,
            overflowX: 'visible',
          }}
        >
          {[["visibility_rate"], ["paid_visibility_rate", "organic_visibility_rate"].filter(metric => totals?.[metric as keyof typeof totals] > 0)].map((metrics) => (
            <Stack
              direction="column"
              spacing={0}
              key={metrics.join('_')}
              bgcolor={"white"}
              borderRadius={1}
              border={"1px solid rgba(145, 158, 171, 0.14)"}
              sx={{ flex: 1, minHeight: 0, overflowY: 'hidden', overflowX: 'visible' }}
            >
              <Stack className="card-top-header" borderBottom={1} borderColor="divider" direction="row" spacing={3} py={1} px={2} justifyContent="space-between">
                {
                  metrics.map((metric, index) => (
                    <>
                      <svg className="card-top-header-item-indicator" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <line x1="1" y1="7" x2="13" y2="7" stroke={palette[metric as keyof typeof palette] as string} stroke-width="2" stroke-linecap="round" />
                      </svg>
                      <Typography className="card-top-header-item-label" flexGrow={index === 0 ? 1 : 0} variant="h6" color="text.primary">{trads[metric as keyof typeof trads]}</Typography>
                    </>
                  ))
                }
              </Stack>
              <Stack
                paddingY={"14px"}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'hidden',
                  overflowX: 'visible',
                  '& .apexcharts-canvas, & .apexcharts-svg': {
                    overflow: 'visible',
                  },
                }}
              >
                <Chart height={"100%"} series={metrics.map((metric) => ({
                  name: metric,
                  data: competitorsGridEvolution?.data?.map((item: any) => ({
                    x: item.week_start,
                    y: item[metric],
                  })) || [],
                }))} type="area" options={useChart({
                  chart: {
                    type: 'area',
                    toolbar: {
                      show: false,
                    },
                  },
                  colors: metrics.map((metric) => palette[metric as keyof typeof palette] as string),
                  legend: {
                    show: false,
                  },
                  xaxis: {
                    categories: competitorsGridEvolution?.data?.map((item: any) => item.week_start) || [],
                    labels: {
                      show: false,
                    },
                    "tooltip": {
                      enabled: false,
                    },
                  },
                  yaxis: {
                    labels: { show: false },
                  },
                  grid: {
                    show: false,
                  },
                  tooltip: {
                    shared: true,
                    intersect: false,
                    custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                      return `
                  <div class="click-split-chart-tooltip">
                  <div class="click-split-chart-tooltip-header">
                  <span class="click-split-chart-tooltip-date">${fDate(competitorsGridEvolution?.data?.[dataPointIndex]?.week_start)}</span>
                  </div>
                  ${metrics?.map((metric) => (
                        `
                      <div class="click-split-chart-tooltip-row">
                      <div class="click-split-chart-tooltip-indicator ${metric.split('_')[0]}"></div>
                      <span class="click-split-chart-tooltip-label">${trads?.[metric as keyof typeof trads]}</span>
                      <span class="click-split-chart-tooltip-value">${fPercent(competitorsGridEvolution?.data?.[dataPointIndex]?.[metric] || 0)}</span>
                      </div>
                      `
                      )).join('\n')
                        }
                  
                  </div>
                  `;
                    }
                  },
                })} />

              </Stack>
              <Stack flexDirection={"row"} px={2} py={1} sx={{backgroundColor: "rgba(145, 158, 171, 0.08)"}}>
                <Typography variant="body2" color="text.secondary">
                  Last 12 weeks
                </Typography>
              </Stack>
            </Stack>
          ))}
        </DialogContent>
      </Dialog>
    </Card>
  );
}